import { useState, useEffect, useCallback } from 'react';

// Convert VAPID public key (url-safe base64) to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

export const isPushSupported = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

export const isStandalonePWA = () => {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
};

export const detectMobileOS = () => {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'other';
};

/**
 * usePushNotifications — registers sw.js, exposes subscribe/unsubscribe/test helpers.
 * @param {object} api - axios instance with auth (from AuthContext)
 */
export function usePushNotifications(api) {
  const [permission, setPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [registration, setRegistration] = useState(null);
  const [loading, setLoading] = useState(false);

  // Register service worker once on mount (idempotent)
  useEffect(() => {
    if (!isPushSupported()) return;
    let cancelled = false;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.register('/sw.js');
        if (cancelled) return;
        setRegistration(reg);
        const existing = await reg.pushManager.getSubscription();
        setIsSubscribed(!!existing);
      } catch (e) {
        console.warn('[Push] SW register failed:', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Listen for navigation messages from SW (when user taps a notification)
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const handler = (e) => {
      if (e.data && e.data.type === 'navigate' && e.data.url) {
        window.location.href = e.data.url;
      }
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, []);

  const subscribe = useCallback(async () => {
    if (!isPushSupported() || !api) return { ok: false, reason: 'unsupported' };
    setLoading(true);
    try {
      // Ensure SW is ready
      const reg = registration || (await navigator.serviceWorker.ready);

      // Request permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') {
        setLoading(false);
        return { ok: false, reason: 'denied' };
      }

      // Get VAPID public key
      const vapidRes = await api.get('/api/push/vapid-public-key');
      const publicKey = vapidRes.data.publicKey;

      // Subscribe to push manager
      let subscription = await reg.pushManager.getSubscription();
      if (!subscription) {
        subscription = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
      }

      // Send to backend
      const json = subscription.toJSON();
      await api.post('/api/push/subscribe', {
        endpoint: json.endpoint,
        keys: json.keys,
        device_info: navigator.userAgent.slice(0, 200),
      });

      setIsSubscribed(true);
      setLoading(false);
      return { ok: true };
    } catch (e) {
      console.error('[Push] subscribe error:', e);
      setLoading(false);
      return { ok: false, reason: 'error', error: e };
    }
  }, [api, registration]);

  const unsubscribe = useCallback(async () => {
    if (!isPushSupported() || !api) return { ok: false };
    setLoading(true);
    try {
      const reg = registration || (await navigator.serviceWorker.ready);
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const ep = sub.endpoint;
        await sub.unsubscribe();
        await api.delete('/api/push/subscribe', { params: { endpoint: ep } });
      }
      setIsSubscribed(false);
      setLoading(false);
      return { ok: true };
    } catch (e) {
      console.error('[Push] unsubscribe error:', e);
      setLoading(false);
      return { ok: false, error: e };
    }
  }, [api, registration]);

  const sendTest = useCallback(async () => {
    if (!api) return { ok: false };
    try {
      const res = await api.post('/api/push/test');
      return { ok: true, data: res.data };
    } catch (e) {
      return { ok: false, error: e };
    }
  }, [api]);

  return {
    isSupported: isPushSupported(),
    permission,
    isSubscribed,
    loading,
    subscribe,
    unsubscribe,
    sendTest,
  };
}
